import { Injectable } from "@angular/core";
import { ProjectService, ProjectStructureService } from "@mydocma/project";
import { filter,startWith,switchMap,take,tap } from "rxjs/operators";

import { BehaviorSubject } from "rxjs/internal/BehaviorSubject";
import { Observable } from "rxjs";
import { StructureNode, StructureTree } from "../structure/structure.model";
import { StructureApiService } from "./structure-api.service";
import { BpViewService } from "../../bp-view.service";
import { TranslateService } from "@ngx-translate/core";

@Injectable({
providedIn: "root"
})
export class StructureService {

  private structuresTreeSubject =  new BehaviorSubject<StructureTree>(null);
  public structuresTree$ =  this.structuresTreeSubject.asObservable();

  private selectedStructureSubject = new BehaviorSubject<StructureNode>(null);
  public selectedStructure$ = this.selectedStructureSubject.asObservable();


  constructor(
    private readonly bpViewService: BpViewService,
    private readonly projectService: ProjectService,
    private readonly structureApiService: StructureApiService,
    private readonly projectStructureService: ProjectStructureService,
    private readonly translateService: TranslateService,
  ) {

    this.bpViewService.currentCustomer$
      .pipe(
        tap(currentCustomer => {
          this.structuresTreeSubject.next(null);
          this.projectStructureService.currentProjectStructureTree = null;
        }),
        switchMap(currentCustomer =>
          this.projectService.currentProject.pipe(
            // forces reevaluation on every customer change
            startWith(null),
            // When using bp in external applications, projectStructureTree may already be set
            // In these cases, structure tree should not be loaded again 
            filter((project) => {
              const currentProjectStructureTree = this.projectStructureService.currentProjectStructureTree;

              if (!currentProjectStructureTree) return true;
              return currentProjectStructureTree.projectId !== (project ? project.id : null);
            }),
            tap(() => this.structuresTreeSubject.next(null)),
            filter(Boolean),
            switchMap(project => this.structureApiService.get(project))
          )
        )
      )
      .subscribe(strTree => {
        this.projectStructureService.currentProjectStructureTree = strTree;
        this.structuresTreeSubject.next(strTree);
        this.selectedStructure = strTree;
      });

    this.projectStructureService.currentProjectStructureTree$$.subscribe(t => this.structuresTreeSubject.next(t));
    this.projectStructureService.currentProjectStructure$.subscribe(s => this.selectedStructureSubject.next(s as StructureNode));


  }

  public set selectedStructure(structure: StructureNode) {
    this.projectStructureService.currentProjectStructure = structure;
    this.selectedStructureSubject.next(structure);
  }
  public get selectedStructure(): StructureNode  {
    return this.selectedStructureSubject.value;
  }

  get structuresTree () : StructureTree {
    return this.structuresTreeSubject.value;
  }

  public async findPlanStructure( tree: StructureTree, plan: any) {
    if (tree !== null) {
        if (plan.structureId !== null) {
            if (tree.id === plan.structureId) {
              plan.structure = tree.name;
              plan.structurePath = tree.path;
            } else {
              const node = await this.getNodeById(tree, plan.structureId);

              if (node !== null && node.id === plan.structureId) {
                  plan.structure = node.name;
                  plan.structurePath = node.path;
              } else {
                  const unassigned = this.translateService.instant('CO.projectStructure.notification.structureUnassigned');
                  plan.structure = unassigned;
                  plan.structurePath = unassigned;
              }
            }
        } else {
            const unassigned = this.translateService.instant('CO.projectStructure.notification.structureUnassigned');
            plan.structure = unassigned;
            plan.structurePath = unassigned;
        }
    }

    return plan;
  }



  getNodeById( tree: StructureTree, structureId: string ): StructureNode {
    if ( tree?.id === structureId ) {
      return tree;
    } else if ( tree?.children?.length > 0 ) {
      let result = null;
      for ( let i = 0; result == null && i < tree?.children.length; i++ ) {
        result =  this.getNodeById(tree?.children[i], structureId);
      }
      return result;
    }
    return null;
  }

  private findByMyDocmaUid(node: StructureNode, myDocmaUid: string): StructureNode | null {
    if (!node || !myDocmaUid) return null;
    if (node.myDocmaUid === myDocmaUid) return node;
    for (const child of node.children ?? []) {
      const found = this.findByMyDocmaUid(child, myDocmaUid);
      if (found) return found;
    }
    return null;
  }

  public refreshStructures(): Observable<StructureTree> {
    return this.projectService.currentProject.pipe(
      take(1),
      filter(p => !!p),
      switchMap(project => this.structureApiService.get(project)),
      tap(strTree => {
        this.projectStructureService.currentProjectStructureTree = strTree;
      })
    );
  }

  public applyGridTreeUpdate(mydocmaNode: { id?: string; myDocmaUid?: string; name?: string; desc?: string }): StructureNode | null {
    const tree = this.structuresTreeSubject.value;
    if (!tree) return null;
    const target = this.locateNode(tree, mydocmaNode?.myDocmaUid, mydocmaNode?.id);
    if (!target) return null;
    const nameChanged = mydocmaNode.name !== undefined && mydocmaNode.name !== target.name;
    target.name = mydocmaNode.name ?? target.name;
    target.desc = mydocmaNode.desc ?? target.desc;
    if (mydocmaNode.myDocmaUid && !target.myDocmaUid) target.myDocmaUid = mydocmaNode.myDocmaUid;
    if (nameChanged) this.recomputePathSubtree(target);
    this.structuresTreeSubject.next(tree);
    return target;
  }

  public applyGridTreeDelete(mydocmaNode: { id?: string; myDocmaUid?: string }): StructureNode | null {
    const tree = this.structuresTreeSubject.value;
    if (!tree) return null;
    const target = this.locateNode(tree, mydocmaNode?.myDocmaUid, mydocmaNode?.id);
    if (!target) return null;
    const parent = this.findParentOf(tree, target);
    if (!parent?.children) return null;
    parent.children = parent.children.filter(c => c !== target);
    this.structuresTreeSubject.next(tree);
    return target;
  }

  public applyGridTreeCreate(node: { id?: string; myDocmaUid?: string; name?: string; desc?: string; parentId?: string; level?: number; sortId?: number }): StructureNode | null {
    const tree = this.structuresTreeSubject.value;
    if (!tree || (!node?.id && !node?.myDocmaUid)) return null;
    const parent = this.locateNode(tree, node.parentId, node.parentId);
    if (!parent) return null;
    if (!parent.children) parent.children = [];
    if (node.myDocmaUid && parent.children.some(c => c.myDocmaUid && c.myDocmaUid === node.myDocmaUid)) return null;
    if (node.id && parent.children.some(c => c.id === node.id)) return null;
    const newNode: StructureNode = {
      id: node.id ?? node.myDocmaUid,
      myDocmaUid: node.myDocmaUid,
      name: node.name ?? '',
      desc: node.desc ?? '',
      level: node.level ?? ((parent.level ?? 0) + 1),
      parentId: parent.id,
      path: '',
      parent,
      children: [],
    };
    const idx = node.sortId;
    if (idx !== undefined && idx >= 0 && idx < parent.children.length) {
      parent.children.splice(idx, 0, newNode);
    } else {
      parent.children.push(newNode);
    }
    this.recomputePathSubtree(newNode);
    this.structuresTreeSubject.next(tree);
    return newNode;
  }

  public applyGridTreeReorder(node: { id?: string; myDocmaUid?: string; parentId?: string; newSortId?: number }): StructureNode | null {
    const tree = this.structuresTreeSubject.value;
    if (!tree || node?.newSortId === undefined) return null;
    const target = this.locateNode(tree, node.myDocmaUid, node.id);
    if (!target) return null;
    const currentParent = this.findParentOf(tree, target);
    if (!currentParent?.children) return null;
    const fromIdx = currentParent.children.indexOf(target);
    if (fromIdx < 0) return null;
    const newParent = node.parentId ? this.locateNode(tree, node.parentId, node.parentId) : currentParent;
    if (!newParent) return null;
    const sameParent = newParent === currentParent;
    currentParent.children.splice(fromIdx, 1);
    if (!newParent.children) newParent.children = [];
    const insertIdx = Math.max(0, Math.min(node.newSortId, newParent.children.length));
    newParent.children.splice(insertIdx, 0, target);
    target.parent = newParent;
    target.parentId = newParent.id;
    if (node.myDocmaUid && !target.myDocmaUid) target.myDocmaUid = node.myDocmaUid;
    if (!sameParent) this.recomputePathSubtree(target);
    this.structuresTreeSubject.next(tree);
    return target;
  }

  private locateNode(tree: StructureNode, myDocmaUid: string | undefined, id: string | undefined): StructureNode | null {
    if (myDocmaUid) {
      const byUid = this.findByMyDocmaUid(tree, myDocmaUid);
      if (byUid) return byUid;
    }
    if (id) {
      const byId = this.getNodeById(tree as StructureTree, id);
      if (byId) return byId;
    }
    return null;
  }

  private findParentOf(tree: StructureNode, target: StructureNode): StructureNode | null {
    if (!tree?.children?.length) return null;
    if (tree.children.includes(target)) return tree;
    for (const child of tree.children) {
      const found = this.findParentOf(child, target);
      if (found) return found;
    }
    return null;
  }

  private recomputePathSubtree(node: StructureNode): void {
    if (!node) return;
    const parent = node.parent;
    node.path = parent ? `${parent.path}/${node.name}` : node.name;
    if (node.children) {
      for (const child of node.children) this.recomputePathSubtree(child);
    }
  }

}
